import { Box, type SxProps, type Theme } from '@mui/material';
import type { SystemProps } from '@mui/system';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
  type UIEvent,
} from 'react';
import { useOnboardingLens } from './onboardingLens';

const hideNativeScrollbarSx: SxProps<Theme> = {
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
};

interface ScrollbarMetrics {
  thumbHeight: number;
  thumbTop: number;
  visible: boolean;
}

function measureScrollbarMetrics(
  contentEl: HTMLElement,
  trackEl: HTMLElement,
  thumbMinHeight: number,
  thumbOffsetTop: number,
): ScrollbarMetrics {
  const { scrollHeight, clientHeight, scrollTop } = contentEl;
  const trackHeight = trackEl.clientHeight;

  if (trackHeight <= 0) {
    return { thumbHeight: 0, thumbTop: 0, visible: false };
  }

  const needsScroll = scrollHeight > clientHeight + 1;

  if (!needsScroll) {
    return {
      thumbHeight: thumbMinHeight,
      thumbTop: thumbOffsetTop,
      visible: true,
    };
  }

  const thumbHeight = Math.max(
    thumbMinHeight,
    (clientHeight / scrollHeight) * trackHeight,
  );
  const maxThumbTop = Math.max(0, trackHeight - thumbHeight);
  const scrollRatio =
    scrollHeight - clientHeight > 0 ? scrollTop / (scrollHeight - clientHeight) : 0;

  return {
    thumbHeight,
    thumbTop: thumbOffsetTop + scrollRatio * maxThumbTop,
    visible: true,
  };
}

function useOnboardingCustomScrollbar(contentRef: RefObject<HTMLElement | null>) {
  const { layout } = useOnboardingLens();
  const { thumb } = layout.scrollbar;
  const trackRef = useRef<HTMLDivElement>(null);
  const [metrics, setMetrics] = useState<ScrollbarMetrics>({
    thumbHeight: thumb.minHeight,
    thumbTop: thumb.offsetTop,
    visible: false,
  });

  const updateMetrics = useCallback(() => {
    const contentEl = contentRef.current;
    const trackEl = trackRef.current;
    if (!contentEl || !trackEl) {
      return;
    }
    setMetrics(measureScrollbarMetrics(contentEl, trackEl, thumb.minHeight, thumb.offsetTop));
  }, [contentRef, thumb.minHeight, thumb.offsetTop]);

  useEffect(() => {
    const contentEl = contentRef.current;
    if (!contentEl) {
      return;
    }

    updateMetrics();

    const resizeObserver = new ResizeObserver(updateMetrics);
    resizeObserver.observe(contentEl);
    for (const child of contentEl.children) {
      resizeObserver.observe(child);
    }

    return () => resizeObserver.disconnect();
  }, [contentRef, updateMetrics]);

  const handleScroll = useCallback(
    (event: UIEvent<HTMLElement>) => {
      const trackEl = trackRef.current;
      if (!trackEl) {
        return;
      }
      setMetrics(
        measureScrollbarMetrics(
          event.currentTarget,
          trackEl,
          thumb.minHeight,
          thumb.offsetTop,
        ),
      );
    },
    [thumb.minHeight, thumb.offsetTop],
  );

  return { trackRef, metrics, handleScroll, updateMetrics };
}

function OnboardingScrollbarTrack({
  trackRef,
  metrics,
}: {
  trackRef: RefObject<HTMLDivElement | null>;
  metrics: ScrollbarMetrics;
}) {
  const { layout } = useOnboardingLens();
  const { track, thumb } = layout.scrollbar;

  return (
    <Box
      ref={trackRef}
      aria-hidden
      sx={{
        flex: 'none',
        width: track.widthCss,
        minWidth: track.widthCss,
        alignSelf: 'stretch',
        position: 'relative',
        backgroundColor: track.backgroundColor,
        boxShadow: track.insetShadow,
      }}
    >
      {metrics.visible ? (
        <Box
          sx={{
            position: 'absolute',
            top: metrics.thumbTop,
            left: thumb.centerOffset,
            width: thumb.widthCss,
            minWidth: thumb.widthCss,
            maxWidth: thumb.widthCss,
            height: metrics.thumbHeight,
            borderRadius: thumb.borderRadius,
            backgroundColor: thumb.color,
          }}
        />
      ) : null}
    </Box>
  );
}

function OnboardingCustomScrollBody({
  contentRef,
  onScroll,
  children,
  sx,
}: {
  contentRef: RefObject<HTMLDivElement | null>;
  onScroll: (event: UIEvent<HTMLElement>) => void;
  children: ReactNode;
  sx?: SxProps<Theme>;
}) {
  return (
    <Box
      ref={contentRef}
      onScroll={onScroll}
      sx={[
        {
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          boxSizing: 'border-box',
          WebkitOverflowScrolling: 'touch',
        },
        hideNativeScrollbarSx,
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {children}
    </Box>
  );
}

interface OnboardingStepScrollLayoutProps {
  header: ReactNode;
  children: ReactNode;
  layout?: 'split' | 'flow' | 'centered';
  /** Lens spacing token between header and body. */
  headerGap?: string;
  contentPaddingBottom?: string;
  px?: SystemProps<Theme>['px'];
  py?: SystemProps<Theme>['py'];
  backgroundColor?: string;
  sx?: SxProps<Theme>;
  bodySx?: SxProps<Theme>;
}

/** Fixed step header + Atlas Lens scroll column — steps 2–6. */
export function OnboardingStepScrollLayout({
  header,
  children,
  layout = 'split',
  headerGap,
  contentPaddingBottom,
  px,
  py,
  backgroundColor = 'var(--lens-semantic-color-surface-default)',
  sx,
  bodySx,
}: OnboardingStepScrollLayoutProps) {
  const { layout: lensLayout } = useOnboardingLens();
  const contentRef = useRef<HTMLDivElement>(null);
  const { trackRef, metrics, handleScroll, updateMetrics } =
    useOnboardingCustomScrollbar(contentRef);

  useEffect(() => {
    updateMetrics();
  }, [children, header, updateMetrics]);

  const headerGapValue = headerGap ?? '0';
  const contentPaddingBottomValue =
    contentPaddingBottom ?? lensLayout.content.paddingBottom;

  if (layout === 'centered') {
    return (
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          height: '100%',
          width: '100%',
          overflow: 'hidden',
          boxSizing: 'border-box',
          backgroundColor,
          ...(py !== undefined ? { pb: lensLayout.formField.labelGap } : {}),
          ...sx,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            width: '100%',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            <Box
              component="header"
              sx={{
                flexShrink: 0,
                width: '100%',
                backgroundColor,
                zIndex: 1,
                px,
                ...(py !== undefined ? { pt: py } : {}),
              }}
            >
              {header}
            </Box>
            <OnboardingCustomScrollBody
              contentRef={contentRef}
              onScroll={handleScroll}
              sx={{
                flex: 1,
                pb: contentPaddingBottomValue,
              }}
            >
              <Box
                sx={{
                  px,
                  width: '100%',
                  minHeight: '100%',
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  ...bodySx,
                }}
              >
                {children}
              </Box>
            </OnboardingCustomScrollBody>
          </Box>
          <OnboardingScrollbarTrack trackRef={trackRef} metrics={metrics} />
        </Box>
      </Box>
    );
  }

  if (layout === 'flow') {
    return (
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          height: '100%',
          width: '100%',
          overflow: 'hidden',
          boxSizing: 'border-box',
          backgroundColor,
          ...(py !== undefined ? { pb: lensLayout.formField.labelGap } : {}),
          ...sx,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            width: '100%',
            overflow: 'hidden',
          }}
        >
          <OnboardingCustomScrollBody
            contentRef={contentRef}
            onScroll={handleScroll}
            sx={{
              flex: 1,
              pb: contentPaddingBottomValue,
            }}
          >
            <Box
              sx={{
                px,
                width: '100%',
                boxSizing: 'border-box',
                ...(py !== undefined ? { pt: py } : {}),
              }}
            >
              {header}
              <Box sx={{ mt: headerGapValue, ...bodySx }}>{children}</Box>
            </Box>
          </OnboardingCustomScrollBody>
          <OnboardingScrollbarTrack trackRef={trackRef} metrics={metrics} />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        minHeight: 0,
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        boxSizing: 'border-box',
        backgroundColor,
        ...(py !== undefined ? { pb: lensLayout.formField.labelGap } : {}),
        ...sx,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          width: '100%',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          <Box
            component="header"
            sx={{
              flexShrink: 0,
              width: '100%',
              backgroundColor,
              zIndex: 1,
              px,
              ...(py !== undefined ? { pt: py } : {}),
            }}
          >
            {header}
          </Box>
          <OnboardingCustomScrollBody
            contentRef={contentRef}
            onScroll={handleScroll}
            sx={{
              flex: 1,
              pt: headerGapValue,
              pb: contentPaddingBottomValue,
            }}
          >
            <Box
              sx={{
                px,
                width: '100%',
                minHeight: '100%',
                boxSizing: 'border-box',
              }}
            >
              <Box sx={{ minHeight: '100%', ...bodySx }}>{children}</Box>
            </Box>
          </OnboardingCustomScrollBody>
        </Box>
        <OnboardingScrollbarTrack trackRef={trackRef} metrics={metrics} />
      </Box>
    </Box>
  );
}
